/**
 * src/types/charts/radar.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Radar / spider chart — multi-dimensional performance visualisation.
 * Each data point is one entity with a value per dimension (axis).
 * All data points must share the same set of dimension keys.
 *
 * FUTURE: renderer not yet implemented — types are production-ready.
 */

import type { ChartDataPoint } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

export type RadarAnimationStyle = 'classic' | 'fadeIn';

export const RADAR_ANIMATION_STYLES: RadarAnimationStyle[] = ['classic', 'fadeIn'];

export const RADAR_ELEMENT_PANEL_STYLES: RadarAnimationStyle[] = [];


// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One entity on the radar chart.
 * `values` maps dimension name → numeric value.
 * All data points must have the same set of dimension keys.
 */
export interface RadarDataPoint extends ChartDataPoint {
  chartType: 'radar';
  values:    Record<string, number>;
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface RadarSpacingConfig {
  chartType:     'radar';
  levels:        number;   // number of concentric grid rings. Range: 2–10. Default: 5.
  labelDistance: number;   // px from outer ring to axis label. Range: 10–60. Default: 20.
  fillOpacity:   number;   // polygon fill opacity. Range: 0–1. Default: 0.3.
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const RADAR_DEFAULTS: ChartConfigBase<RadarSpacingConfig> = {
  chartType: 'radar',
  content: {
    titles:    [makeTitle('Multi-Dimension Analysis')],
    subtitles: [makeSubtitle('Performance Across Dimensions')],
    infoBoxes: [],
  },
  animation:  { duration: 5, style: 'classic', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout: {
    chartPosition: { x: 0, y: 0 },
    chartMargin:   { top: 150, right: 120, bottom: 120, left: 120 },
  },
  // No traditional x/y axes.
  legend:     { ...DEFAULT_LEGEND },
  dataLabels: { ...DEFAULT_DATA_LABELS, visible: false },
  spacing: {
    chartType:     'radar',
    levels:        5,
    labelDistance: 20,
    fillOpacity:   0.3,
  },
  export: { quality: '1080p', isLooping: false, canvasFormat: '16:9' },
};
